import api from './api';
import { MaintenanceOffer } from './maintenanceOfferService';

// Miroir de l'ENUM `subscriptions.status` côté API (models/Subscription.js)
export type SubscriptionStatus =
  | 'pending_payment'
  | 'active'
  | 'awaiting_second_payment'
  | 'completed'
  | 'used'
  | 'expired'
  | 'cancelled';

// Miroir de l'ENUM `subscriptions.payment_status` ('partial' = acompte 50 % versé)
export type SubscriptionPaymentStatus = 'pending' | 'partial' | 'paid' | 'failed';

export interface Subscription {
  id: number;
  customer_id: number;
  maintenance_offer_id?: number | null;
  installation_service_id?: number | null;
  repair_service_id?: number | null;
  status: SubscriptionStatus;
  start_date: string;
  end_date: string;
  price: number;
  original_price?: number | null;
  discount_amount?: number | null;
  promo_code?: string | null;
  payment_status: SubscriptionPaymentStatus;
  // Contrat annuel (contract_type = 'scheduled')
  contract_type?: 'on_demand' | 'scheduled';
  equipment_count?: number;
  equipment_description?: string | null;
  equipment_model?: string | null;
  visits_total?: number;
  visits_completed?: number;
  visit_interval_months?: number;
  first_intervention_date?: string | null;
  next_visit_date?: string | null;
  // Conditions commerciales figées à la souscription
  offer_snapshot?: {
    offer_id: number;
    title: string;
    offer_type: 'on_demand' | 'annual';
    equipment_category?: string;
    pricing_mode?: 'per_equipment' | 'flat';
    unit_price?: number;
    duration_months?: number;
    visits_total?: number;
    visit_interval_months?: number;
    annual_discount_percentage?: number;
    includes_freon?: boolean;
    captured_at?: string;
  } | null;
  // Échéancier 50 / 50
  first_payment_amount?: number | null;
  first_payment_status?: 'pending' | 'paid' | null;
  second_payment_amount?: number | null;
  second_payment_status?: 'pending' | 'paid' | null;
  payment_reference?: string | null;
  payment_date?: string | null;
  created_at: string;
  updated_at: string;
  // Relations
  customer?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  offer?: MaintenanceOffer;
  installationService?: {
    id: number;
    title: string;
    model: string;
    description: string;
    price: number;
    duration?: number;
    isActive: boolean;
  };
  repairService?: {
    id: number;
    title: string;
    model: string;
    description: string;
    price: number;
    duration?: number;
    isActive: boolean;
  };
}

export const subscriptionsService = {
  // Récupérer toutes les souscriptions (admin)
  getAll: async (): Promise<Subscription[]> => {
    const response = await api.get('/admin/subscriptions');
    return response.data.data || response.data;
  },

  // Récupérer une souscription par ID
  getById: async (id: number): Promise<Subscription> => {
    const response = await api.get(`/admin/subscriptions/${id}`);
    return response.data.data || response.data;
  },

  // Annuler une souscription
  cancel: async (id: number): Promise<void> => {
    await api.patch(`/admin/subscriptions/${id}/cancel`);
  },

  // Mettre à jour le statut de paiement
  updatePaymentStatus: async (id: number, paymentStatus: 'pending' | 'paid' | 'failed'): Promise<Subscription> => {
    const response = await api.patch(`/admin/subscriptions/${id}/payment-status`, { payment_status: paymentStatus });
    return response.data.data || response.data;
  },

  // Supprimer une souscription (échec de paiement ou annulée)
  delete: async (id: number): Promise<void> => {
    await api.delete(`/admin/subscriptions/${id}`);
  },

  // Renouveler une souscription (expirée ou annulée)
  renew: async (id: number): Promise<Subscription> => {
    const response = await api.post(`/admin/subscriptions/${id}/renew`);
    return response.data.data || response.data;
  }
};
